import * as React from 'react'

import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { Delegation } from '@/app/types/creator'
import { CreatorGenerationButtonMode } from './delegationGeneration'
import { DelegationsRowItem } from './DelegationsRowItem'

export type DelegationsDriverRow = {
  driverId: string
  name: string
  surname: string
  summary?: {
    totalTime: string
    totalOvertime: string
    totalAccommodation: number
    totalAllowances: number
  }
  delegations?: Delegation[]
}

const getColumns = (
  t: (key: string) => string,
  expanded: boolean,
): TableHeaderColumn[] => {
  if (!expanded) {
    return [
      {
        title: t('common.driver'),
        key: 'surname',
        secondaryKey: 'name',
        type: 'string',
        sort: true,
      },
      { title: '', key: null, type: 'string', sort: false },
    ]
  }
  return [
    {
      title: t('common.driver'),
      key: 'surname',
      secondaryKey: 'name',
      type: 'string',
      sort: true,
    },
    {
      title: t('creator.total-time'),
      key: 'summary.totalTime',
      type: 'duration',
      sort: true,
    },
    {
      title: t('creator.total-accommodation'),
      key: 'summary.totalAccommodation',
      type: 'number',
      sort: true,
    },
    {
      title: t('creator.total-allowances'),
      key: 'summary.totalAllowances',
      type: 'number',
      sort: true,
    },
    { title: '', key: null, type: 'string', sort: false },
  ]
}

type Props = {
  drivers: DelegationsDriverRow[]
  expandedRow?: string
  toggleRow: (driverId: string) => void
  onGenerateForDriver: (
    driverId: string,
    mode: CreatorGenerationButtonMode,
  ) => void
  processingDriverIds: Set<string>
  generationErrors?: Record<string, string>
  onRemoveFromSettlement?: (driverId: string) => void
  renderErrorAction?: (driverId: string) => React.ReactNode
  renderDetailRowActions: (
    delegation: Delegation,
    driverId: string,
  ) => React.ReactNode
}

export const DelegationsGroupedTable = ({
  drivers,
  expandedRow,
  toggleRow,
  onGenerateForDriver,
  processingDriverIds,
  generationErrors,
  onRemoveFromSettlement,
  renderErrorAction,
  renderDetailRowActions,
}: Props) => {
  const { t } = useLocalize()

  const allDriversHaveDelegations = React.useMemo(
    () => drivers.length > 0 && drivers.every((d) => !!d.delegations?.length),
    [drivers],
  )

  const columns = React.useMemo(
    () => getColumns(t, allDriversHaveDelegations),
    [t, allDriversHaveDelegations],
  )

  const { sortedData, sortConfig, handleHeaderClick } =
    useSortableData<DelegationsDriverRow>({
      data: drivers,
      tableColumns: columns,
    })

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      {drivers.length > 0 ? (
        <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto">
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              sticky
              columns={columns}
              sortConfig={sortConfig}
              disabledCheckboxToggle
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {sortedData.map((driver) => (
                <DelegationsRowItem
                  key={driver.driverId}
                  driver={driver}
                  columnsCount={columns.length}
                  isExpanded={expandedRow === driver.driverId}
                  isLoading={processingDriverIds.has(driver.driverId)}
                  showSummary={allDriversHaveDelegations}
                  errorMessage={generationErrors?.[driver.driverId]}
                  onToggle={() => toggleRow(driver.driverId)}
                  onGenerate={(mode) =>
                    onGenerateForDriver(driver.driverId, mode)
                  }
                  onRemoveFromSettlement={
                    onRemoveFromSettlement
                      ? () => onRemoveFromSettlement(driver.driverId)
                      : undefined
                  }
                  renderErrorAction={renderErrorAction}
                  renderDetailRowActions={renderDetailRowActions}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <TableHeader
            opacity
            disabledCheckboxToggle
            columns={columns}
            sortConfig={null}
          />
          <TableEmpty title={t('driverModule.no-drivers')} />
        </>
      )}
    </section>
  )
}
